import { BudgetOnTopCostAttributes, BudgetOnTopCostValueAttributes } from '@callmart/types';
import { extractBlobErrorMessage } from '../utils/fileUtils';
import api from './index';

const BASE = '/budget';

export interface OnTopCostWithRelations extends BudgetOnTopCostAttributes {
    values: BudgetOnTopCostValueAttributes[];
}

export const getOnTopCosts = async (versionId: number): Promise<OnTopCostWithRelations[]> => {
    return api.get(`${BASE}/versions/${versionId}/on-top-costs`).then((res) => res.data);
};

export const createOnTopCost = async (versionId: number, data: Record<string, unknown>) => {
    return api.post(`${BASE}/versions/${versionId}/on-top-costs`, data).then((res) => res.data);
};

export const bulkCreateOnTopCosts = async (versionId: number, costs: Record<string, unknown>[]) => {
    return api.post(`${BASE}/versions/${versionId}/on-top-costs/bulk`, { costs }).then((res) => res.data);
};

export const updateOnTopCost = async ({ id, ...data }: { id: number; [key: string]: unknown }) => {
    return api.patch(`${BASE}/on-top-costs/${id}`, data).then((res) => res.data);
};

export const deleteOnTopCost = async (id: number) => {
    return api.delete(`${BASE}/on-top-costs/${id}`).then((res) => res.data);
};

/** One on-top cost's share of the Overview's pending edits. `value: null` clears that month. */
export interface OnTopCostEdit {
    id: number;
    fields?: Record<string, unknown>;
    valueEdits?: Array<{ month: string; value: number | null }>;
}

// CALL-779: the Overview's On Top Costs grid saves its pending edits together, in one transaction.
// Month-level semantics, unlike updateOnTopCost above, whose `values` replaces the whole series.
export const updateOnTopCostsBulk = async (costs: OnTopCostEdit[]): Promise<OnTopCostWithRelations[]> => {
    return api.patch(`${BASE}/on-top-costs/bulk`, { costs }).then((res) => res.data);
};

export interface ImportOnTopCostsResponse {
    created: OnTopCostWithRelations[];
    createdCount: number;
    // CALL-757: the import fully replaces the version's on-top costs with the file. replacedCount =
    // existing rows removed; createdCount = rows inserted from the file; skippedCount = rows that
    // failed validation (CALL-755, kept out of the file's effect).
    replacedCount: number;
    skippedCount: number;
    warnings: string[];
    monthColumns: string[];
    rowsParsed: number;
}

export const importOnTopCosts = async (versionId: number, file: File): Promise<ImportOnTopCostsResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`${BASE}/versions/${versionId}/on-top-costs/import`, formData).then((res) => res.data);
};

// CALL-757: export a version's on-top costs in the import format (round-trips through the importer).
export const exportOnTopCosts = async (versionId: number): Promise<Blob> => {
    try {
        const res = await api.get(`${BASE}/versions/${versionId}/on-top-costs/export`, { responseType: 'blob' });
        return res.data;
    } catch (error) {
        const message = await extractBlobErrorMessage(error);
        if (message && error instanceof Error) error.message = message;
        throw error;
    }
};

// CALL-750: sample import template (global, version-agnostic) generated server-side from the same
// header set the parser keys off, so it always matches what the import accepts.
export const downloadOnTopCostsImportTemplate = async (): Promise<Blob> => {
    try {
        const res = await api.get(`${BASE}/on-top-costs/import-template`, { responseType: 'blob' });
        return res.data;
    } catch (error) {
        const message = await extractBlobErrorMessage(error);
        if (message && error instanceof Error) error.message = message;
        throw error;
    }
};
